import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { UserPlus, Check, X, Loader2, ChevronLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";

interface WorkspaceMember {
  id: string;
  user_id: string;
  role: string;
  display_name?: string | null;
  default_funnel_id?: string | null;
  default_stage_slug?: string | null;
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
  };
}

type FunnelStage = {
  slug: string;
  name: string;
  order_index: number;
};

type SalesFunnelOption = {
  id: string;
  name: string;
  order_index: number | null;
  is_default: boolean | null;
};

type AssignLeadResult = {
  lead_id: string;
  assigned_member_id: string;
  funnel_id: string | null;
  funnel_name: string | null;
  stage_slug?: string | null;
  stage_name?: string | null;
  moved_funnel: boolean;
};

export type MemberAssignPopoverHandle = { open: () => void };

interface MemberAssignPopoverProps {
  workspaceId: string;
  leadId: string;
  currentMemberId?: string | null;
  currentMemberIds?: string[];
  onAssign?: (memberIds: string[], memberLabels?: string[]) => void;
  compact?: boolean;
  conversationId?: string | null;
  actingMemberId?: string | null;
  /** Sem botão; abre via ref.open() — usa Dialog (melhor no mobile). */
  hideTrigger?: boolean;
  /** Com hideTrigger: exibe botão que abre o Dialog. */
  showTrigger?: boolean;
}

export const MemberAssignPopover = forwardRef<
  MemberAssignPopoverHandle | null,
  MemberAssignPopoverProps
>(function MemberAssignPopover(
  {
    workspaceId,
    leadId,
    currentMemberId,
    currentMemberIds,
    onAssign,
    compact = false,
    hideTrigger = false,
    showTrigger = false,
  },
  ref
) {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [funnels, setFunnels] = useState<SalesFunnelOption[]>([]);
  const [funnelNames, setFunnelNames] = useState<Map<string, string>>(new Map());
  const [stagesByFunnelId, setStagesByFunnelId] = useState<Map<string, FunnelStage[]>>(
    new Map()
  );
  const [allowedFunnelsByMemberId, setAllowedFunnelsByMemberId] = useState<
    Map<string, Set<string>>
  >(new Map());
  const [assignedMemberIds, setAssignedMemberIds] = useState<Set<string>>(() => new Set());
  const [open, setOpen] = useState(false);
  const [assigningMemberId, setAssigningMemberId] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersLoadError, setMembersLoadError] = useState<string | null>(null);
  const [stagePickerMember, setStagePickerMember] = useState<WorkspaceMember | null>(null);
  const { toast } = useToast();

  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);

  useEffect(() => {
    const initial = new Set(currentMemberIds ?? []);
    if (currentMemberId) initial.add(currentMemberId);
    setAssignedMemberIds(initial);
  }, [currentMemberId, currentMemberIds, leadId]);

  useEffect(() => {
    if (!open || !leadId) return;

    const fetchAssignments = async () => {
      const { data, error } = await supabase
        .from("lead_member_assignments")
        .select("member_id")
        .eq("lead_id", leadId);

      if (error) {
        console.error("Erro ao carregar atribuições do lead:", error);
        return;
      }

      const ids = new Set((data ?? []).map((row) => row.member_id));
      if (currentMemberId) ids.add(currentMemberId);
      setAssignedMemberIds(ids);
    };

    void fetchAssignments();
  }, [open, leadId, currentMemberId]);

  useEffect(() => {
    if (!open) {
      setAssigningMemberId(null);
      setMembersLoadError(null);
      setStagePickerMember(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !workspaceId) return;

    const fetchMembers = async () => {
      setLoadingMembers(true);
      setMembersLoadError(null);

      try {
        const membersRes = await supabase
          .from("workspace_members")
          .select("id, user_id, role, display_name, default_funnel_id, default_stage_slug")
          .eq("workspace_id", workspaceId);

        if (membersRes.error) {
          throw membersRes.error;
        }

        const memberRows = membersRes.data ?? [];

        const funnelsRes = await supabase
          .from("sales_funnels")
          .select("id, name, order_index, is_default")
          .eq("workspace_id", workspaceId)
          .order("order_index", { ascending: true });

        if (funnelsRes.error) {
          throw funnelsRes.error;
        }

        const funnelRows = (funnelsRes.data || []) as SalesFunnelOption[];
        const allFunnelIds = funnelRows.map((f) => f.id);

        let stagesMap = new Map<string, FunnelStage[]>();
        if (allFunnelIds.length > 0) {
          const { data: stagesData } = await supabase
            .from("lead_stages")
            .select("slug, name, order_index, funnel_id")
            .in("funnel_id", allFunnelIds)
            .order("order_index", { ascending: true });

          stagesMap = (stagesData || []).reduce((acc, stage) => {
            if (!stage.funnel_id) return acc;
            const list = acc.get(stage.funnel_id) || [];
            list.push({
              slug: stage.slug,
              name: stage.name,
              order_index: stage.order_index,
            });
            acc.set(stage.funnel_id, list);
            return acc;
          }, new Map<string, FunnelStage[]>());
        }

        const memberIds = memberRows.map((m) => m.id);
        let allowedMap = new Map<string, Set<string>>();
        if (memberIds.length > 0) {
          const { data: memberFunnelRows } = await supabase
            .from("workspace_member_funnels")
            .select("member_id, funnel_id")
            .in("member_id", memberIds);

          allowedMap = (memberFunnelRows || []).reduce((acc, row) => {
            const set = acc.get(row.member_id) || new Set<string>();
            set.add(row.funnel_id);
            acc.set(row.member_id, set);
            return acc;
          }, new Map<string, Set<string>>());
        }

        const userIds = memberRows.map((m) => m.user_id);
        const { data: profiles } =
          userIds.length > 0
            ? await supabase
                .from("profiles")
                .select("id, full_name, avatar_url")
                .in("id", userIds)
            : { data: [] as { id: string; full_name: string | null; avatar_url: string | null }[] };

        const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);
        setMembers(
          memberRows.map((m) => ({
            ...m,
            default_funnel_id: m.default_funnel_id ?? null,
            default_stage_slug: m.default_stage_slug ?? null,
            profile: (profileMap.get(m.user_id) as WorkspaceMember["profile"]) || null,
          }))
        );

        setFunnels(funnelRows);
        setFunnelNames(new Map(funnelRows.map((f) => [f.id, f.name])));
        setStagesByFunnelId(stagesMap);
        setAllowedFunnelsByMemberId(allowedMap);
      } catch (error: unknown) {
        console.error("Erro ao carregar membros para atribuição:", error);
        setMembers([]);
        setFunnels([]);
        setAllowedFunnelsByMemberId(new Map());
        setMembersLoadError(
          humanizeSupabaseError(error, {
            fallback: "Não foi possível carregar a equipe. Tente novamente.",
          })
        );
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [open, workspaceId]);

  const memberDisplayName = (member: WorkspaceMember) =>
    member.display_name || member.profile?.full_name || "Sem nome";

  const notifyAssigneesChanged = (ids: Set<string>) => {
    const labels = members
      .filter((m) => ids.has(m.id))
      .map((m) => memberDisplayName(m));
    onAssign?.([...ids], labels);
  };

  const handleRemoveAssign = async () => {
    try {
      const { error } = await supabase.rpc("remove_all_lead_assignees", {
        p_lead_id: leadId,
      });

      if (error) throw error;

      setAssignedMemberIds(new Set());
      onAssign?.([], []);
      setOpen(false);

      toast({
        title: "✅ Atribuições removidas",
        description: "Todos os membros foram desatribuídos deste lead.",
      });
    } catch (error: unknown) {
      toast({
        title: "Erro ao atribuir",
        description: humanizeSupabaseError(error, {
          permissionMessage:
            "Você não tem permissão para remover a atribuição deste lead.",
        }),
        variant: "destructive",
      });
    }
  };

  const handleRemoveMember = async (member: WorkspaceMember) => {
    if (assigningMemberId) return;
    setAssigningMemberId(member.id);
    try {
      const { error } = await supabase.rpc("remove_lead_assignee", {
        p_lead_id: leadId,
        p_member_id: member.id,
      });
      if (error) throw error;

      const next = new Set(assignedMemberIds);
      next.delete(member.id);
      setAssignedMemberIds(next);
      notifyAssigneesChanged(next);
      toast({
        title: "Membro removido",
        description: `${memberDisplayName(member)} foi desatribuído deste lead.`,
      });
    } catch (error: unknown) {
      toast({
        title: "Erro ao remover atribuição",
        description: humanizeSupabaseError(error),
        variant: "destructive",
      });
    } finally {
      setAssigningMemberId(null);
    }
  };

  const resolveDefaultStageLabel = (member: WorkspaceMember) => {
    if (!member.default_funnel_id) return null;
    const stages = stagesByFunnelId.get(member.default_funnel_id) || [];
    if (member.default_stage_slug) {
      return stages.find((s) => s.slug === member.default_stage_slug)?.name || member.default_stage_slug;
    }
    return stages[0]?.name || "Primeira coluna";
  };

  const getAvailableFunnelsForMember = (member: WorkspaceMember) => {
    if (member.role === "owner" || member.role === "admin") return funnels;
    const allowed = allowedFunnelsByMemberId.get(member.id);
    if (!allowed || allowed.size === 0) return funnels;
    return funnels.filter((funnel) => allowed.has(funnel.id));
  };

  const handleAssignToMember = async (
    member: WorkspaceMember,
    options?: { funnelId?: string | null; stageSlug?: string | null }
  ) => {
    if (assigningMemberId) return;

    setAssigningMemberId(member.id);
    try {
      try {
        const { data: meMember } = await supabase
          .from("workspace_members")
          .select("id")
          .eq("workspace_id", workspaceId)
          .eq("user_id", (await supabase.auth.getUser()).data.user?.id ?? "")
          .maybeSingle();
        if (meMember?.id) {
          await supabase.rpc("set_acting_member_id", { _member_id: meMember.id });
        }
      } catch {
        /* opcional */
      }

      const rpcArgs: {
        p_lead_id: string;
        p_target_member_id: string;
        p_funnel_id?: string;
        p_stage_slug?: string;
      } = {
        p_lead_id: leadId,
        p_target_member_id: member.id,
      };

      if (options?.funnelId) {
        rpcArgs.p_funnel_id = options.funnelId;
      }
      if (options?.stageSlug) {
        rpcArgs.p_stage_slug = options.stageSlug;
      }

      const { data, error } = await supabase.rpc("assign_lead_to_member", rpcArgs);

      if (error) throw error;

      const result = data as AssignLeadResult | null;
      const funnelName =
        result?.funnel_name ||
        (result?.funnel_id ? funnelNames.get(result.funnel_id) : null) ||
        (member.default_funnel_id ? funnelNames.get(member.default_funnel_id) : null);
      const stageName =
        result?.stage_name ||
        (result?.stage_slug
          ? stagesByFunnelId
              .get(result.funnel_id || member.default_funnel_id || "")
              ?.find((s) => s.slug === result.stage_slug)?.name
          : null);

      const next = new Set(assignedMemberIds);
      next.add(member.id);
      setAssignedMemberIds(next);
      notifyAssigneesChanged(next);
      setOpen(false);
      setStagePickerMember(null);

      let description = "Lead atribuído ao membro da equipe.";
      if (result?.moved_funnel && funnelName && stageName) {
        description = `Lead atribuído e movido para "${funnelName}" → ${stageName}.`;
      } else if (result?.moved_funnel && funnelName) {
        description = `Lead atribuído e movido para o funil "${funnelName}".`;
      } else if (funnelName && stageName) {
        description = `Lead atribuído (permanece em "${funnelName}" → ${stageName}).`;
      } else if (funnelName) {
        description = `Lead atribuído ao membro da equipe (permanece no funil "${funnelName}").`;
      }

      toast({
        title: "✅ Membro atribuído",
        description,
      });
    } catch (error: unknown) {
      toast({
        title: "Erro ao atribuir",
        description: humanizeSupabaseError(error, {
          permissionMessage:
            "Você não tem permissão para atribuir ou mover este lead.",
          fallback: "Não foi possível atribuir o lead. Tente novamente.",
        }),
        variant: "destructive",
      });
    } finally {
      setAssigningMemberId(null);
    }
  };

  const handleMemberClick = (member: WorkspaceMember) => {
    if (assigningMemberId) return;
    if (assignedMemberIds.has(member.id)) {
      void handleRemoveMember(member);
      return;
    }
    if (getAvailableFunnelsForMember(member).length > 0 && assignedMemberIds.size === 0) {
      setStagePickerMember(member);
      return;
    }
    void handleAssignToMember(member);
  };

  const initials = (name: string | null | undefined) =>
    (name || "?")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const availableFunnelsForStagePicker = stagePickerMember
    ? getAvailableFunnelsForMember(stagePickerMember)
    : [];

  const stagePickerStep = stagePickerMember ? (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setStagePickerMember(null)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Voltar
      </button>
      <p className="text-xs font-medium px-2.5 pb-1">
        {memberDisplayName(stagePickerMember)}
      </p>
      <p className="text-[11px] text-muted-foreground px-2.5 pb-2">
        Escolha o funil e a coluna de destino para este lead.
      </p>
      <div className="space-y-0.5 max-h-[min(50vh,280px)] overflow-y-auto">
        {stagePickerMember.default_funnel_id && (
          <button
            type="button"
            disabled={!!assigningMemberId}
            onClick={() => handleAssignToMember(stagePickerMember)}
            className={cn(
              "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors hover:bg-muted",
              assigningMemberId && "opacity-50"
            )}
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-primary/10">
              <ArrowRight className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Coluna padrão</p>
              <p className="text-[10px] text-muted-foreground truncate">
                {funnelNames.get(stagePickerMember.default_funnel_id)} → {resolveDefaultStageLabel(stagePickerMember)}
              </p>
            </div>
            {assigningMemberId === stagePickerMember.id && (
              <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
            )}
          </button>
        )}
        {availableFunnelsForStagePicker.map((funnel) => {
          const stages = stagesByFunnelId.get(funnel.id) || [];
          return (
            <div key={funnel.id} className="pt-1 first:pt-0">
              <p className="px-2.5 pb-1 pt-1 text-[11px] font-medium text-muted-foreground truncate">
                {funnel.name}
              </p>
              {stages.length === 0 ? (
                <button
                  type="button"
                  disabled={!!assigningMemberId}
                  onClick={() =>
                    handleAssignToMember(stagePickerMember, {
                      funnelId: funnel.id,
                    })
                  }
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors hover:bg-muted",
                    assigningMemberId && assigningMemberId !== stagePickerMember.id && "opacity-50"
                  )}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-muted">
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium truncate flex-1">Primeira coluna</p>
                  {assigningMemberId === stagePickerMember.id && (
                    <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                  )}
                </button>
              ) : (
                stages.map((stage) => (
                  <button
                    type="button"
                    key={`${funnel.id}:${stage.slug}`}
                    disabled={!!assigningMemberId}
                    onClick={() =>
                      handleAssignToMember(stagePickerMember, {
                        funnelId: funnel.id,
                        stageSlug: stage.slug,
                      })
                    }
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors hover:bg-muted",
                      assigningMemberId && assigningMemberId !== stagePickerMember.id && "opacity-50"
                    )}
                  >
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-muted">
                      <span className="text-[10px] font-medium text-muted-foreground">
                        {stage.order_index + 1}
                      </span>
                    </div>
                    <p className="text-sm font-medium truncate flex-1">{stage.name}</p>
                    {assigningMemberId === stagePickerMember.id && (
                      <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  const membersStep = (
    <div className="space-y-0.5 max-h-[min(60vh,320px)] overflow-y-auto">
      <p className="text-xs text-muted-foreground px-2.5 pb-2">
        Selecione um ou mais membros. Toque novamente para remover. Na primeira atribuição, escolha funil e coluna se necessário.
      </p>
      {loadingMembers && (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {!loadingMembers && membersLoadError && (
        <p className="text-xs text-destructive px-2.5 py-3 text-center">{membersLoadError}</p>
      )}
      {!loadingMembers && !membersLoadError && members.length === 0 && (
        <p className="text-xs text-muted-foreground px-2.5 py-3 text-center">
          Nenhum membro encontrado neste workspace.
        </p>
      )}
      {assignedMemberIds.size > 0 && (
        <button
          type="button"
          onClick={handleRemoveAssign}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors text-destructive hover:bg-destructive/10"
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-destructive/10">
            <X className="w-3.5 h-3.5" />
          </div>
          <p className="text-sm font-medium">Remover todas as atribuições</p>
        </button>
      )}
      {!loadingMembers &&
        !membersLoadError &&
        members.map((member) => {
        const isSelected = assignedMemberIds.has(member.id);
        const isAssigning = assigningMemberId === member.id;
        const name = memberDisplayName(member);
        const defaultFunnelLabel = member.default_funnel_id
          ? funnelNames.get(member.default_funnel_id)
          : null;
        const defaultStageLabel = member.default_funnel_id
          ? resolveDefaultStageLabel(member)
          : null;

        return (
          <button
            type="button"
            key={member.id}
            disabled={!!assigningMemberId}
            onClick={() => handleMemberClick(member)}
            className={cn(
              "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors",
              isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted",
              assigningMemberId && !isAssigning && "opacity-50"
            )}
          >
            <Avatar className="w-7 h-7 flex-shrink-0">
              {member.profile?.avatar_url ? (
                <img
                  src={member.profile.avatar_url}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {initials(name)}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{name}</p>
              {defaultFunnelLabel && (
                <p className="text-[10px] text-muted-foreground truncate">
                  Funil: {defaultFunnelLabel}
                  {defaultStageLabel ? ` → ${defaultStageLabel}` : ""}
                </p>
              )}
            </div>
            {isAssigning ? (
              <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
            ) : (
              isSelected && <Check className="w-4 h-4 text-primary flex-shrink-0" />
            )}
          </button>
        );
      })}
    </div>
  );

  const pickerTitle = stagePickerMember ? "Escolher coluna" : "Atribuir a membro";
  const pickerBody = stagePickerMember ? stagePickerStep : membersStep;

  if (hideTrigger) {
    return (
      <>
        {showTrigger ? (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            title="Atribuir membro"
            onClick={() => setOpen(true)}
            className={cn(
              compact ? "h-7 w-7" : "h-8 w-8",
              "flex-shrink-0",
              assignedMemberIds.size > 0 && "text-primary"
            )}
          >
            <UserPlus className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
          </Button>
        ) : null}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-sm p-4 gap-3">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-base">{pickerTitle}</DialogTitle>
            </DialogHeader>
            {pickerBody}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          title="Atribuir membro"
          className={cn(
            compact ? "h-7 w-7" : "h-8 w-8",
            "flex-shrink-0",
            assignedMemberIds.size > 0 && "text-primary"
          )}
        >
          <UserPlus className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end" sideOffset={8}>
        <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">
          {pickerTitle}
        </p>
        {pickerBody}
      </PopoverContent>
    </Popover>
  );
});
